import { theme } from 'antd';
import { DeletedFilled } from 'assets/images/svg';
import { useManagePermittedRoles } from 'library/hooks';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectTeams } from 'reduxStore/teams/selectors';
import { teamsActions } from 'reduxStore/teams/slice';
import { Notification } from 'zComponents/atoms';
import { Button, Divider, Flex, Modal, Space, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const DeleteTeamsModal = ({ children, teamIDs = [], visible: visibleProp, setVisible: setVisibleProp }) => {
	// ✅ Hooks
	const canDeleteTeam = useManagePermittedRoles('deleteTeam');
	const permissionDeniedMessage = "You don't have permission to perform this action.";
	const {
		token: { paddingLG, fontSizeSM, marginXL, marginXS, paddingXL, paddingXS, paddingSM }
	} = theme.useToken();
	const dispatch = useDispatch();

	// ✅ Local State
	const [internalVisible, setInternalVisible] = useState(false);

	// ✅ If visible is not provided, use internal state
	const visible = visibleProp !== undefined ? visibleProp : internalVisible;
	const setVisible = setVisibleProp !== undefined ? setVisibleProp : setInternalVisible;

	// ✅ Get all teams from store
	const teams = useSelector(selectTeams);

	// ✅ Get loading and success states
	const { deleteTeamLoading: loading } = useSelector((state) => state.Teams);

	// ✅ Filter selected teams
	const filteredTeams = useMemo(() => teams.filter((team) => teamIDs.includes(team.teamID)), [teams, teamIDs]);

	// ✅ Generate selected team names
	const selectedNames = useMemo(() => filteredTeams.map((team) => team.name || '-'), [filteredTeams]);

	// ✅ Generate dynamic messages
	const generateDeleteTitle = () =>
		teamIDs.length > 1
			? `Are you sure you want to delete these ${teamIDs.length} teams?`
			: 'Are you sure you want to delete this team?';

	const generateDeleteButtonText = () => (teamIDs.length > 1 ? 'Delete Teams' : 'Delete');

	// ✅ Close modal
	const handleCancel = useCallback(() => setVisible(false), [setVisible]);

	// ✅ Handle deletion
	const handleDelete = () => {
		teamIDs.forEach((teamID) => {
			dispatch(teamsActions.deleteTeam({ teamID }));
		});

		setTimeout(() => {
			Notification('success', 'Team(s) have been delete successfully.');
			handleCancel();
		}, 500);
	};

	return (
		<>
			<Tooltip title={!canDeleteTeam ? permissionDeniedMessage : 'Delete this Team'} placement='top'>
				<span onClick={(e) => canDeleteTeam && setVisible(true)}>
					{children ? (
						children
					) : (
						<Button
							type='text'
							size='small'
							icon={<DeletedFilled color='currentColor' width={16} height={16} />}
							onClick={(e) => {
								e.stopPropagation();
								canDeleteTeam && setVisible(true);
							}}
							disabled={loading || !canDeleteTeam}
						/>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='center'
						style={{ marginTop: marginXL, padding: `0px ${paddingXS}px` }}
						gap={12}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={loading} block>
							Cancel
						</Button>
						<Button onClick={handleDelete} loading={loading} disabled={loading} type='primary' danger block>
							{generateDeleteButtonText()}
						</Button>
					</Flex>
				]}
				width={350}
				styles={{ content: { padding: paddingXL } }}
			>
				<Flex vertical align='center' justify='center' gap={paddingSM}>
					<DeletedFilled />
					<Flex vertical gap={paddingLG}>
						<Space
							size={paddingXS}
							direction='vertical'
							align='center'
							style={{ margin: `${paddingXS}px 0px` }}
						>
							<Title
								level={5}
								style={{
									margin: 0,
									lineHeight: '24px',
									textAlign: 'center',
									padding: `0px ${paddingXL}px`
								}}
							>
								{generateDeleteTitle()}
							</Title>
							<Text
								style={{
									fontSize: fontSizeSM,
									lineHeight: '24px',
									textAlign: 'center',
									display: 'block'
								}}
							>
								This action is permanent and <br /> cannot be undone.
							</Text>
						</Space>

						<Flex vertical gap={marginXS}>
							<Divider orientation='center' plain style={{ margin: 0, padding: 0, marginTop: 0 }}>
								<Text style={{ fontSize: fontSizeSM, fontWeight: 200 }}>
									{teamIDs.length > 1 ? 'Selected Teams' : 'Selected Team'}
								</Text>
							</Divider>
							<Text style={{ fontSize: fontSizeSM, fontWeight: 200, textAlign: 'center' }}>
								{selectedNames.join(', ')}
							</Text>
						</Flex>
					</Flex>
				</Flex>
			</Modal>
		</>
	);
};

export default DeleteTeamsModal;
